import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subscription, filter, firstValueFrom, fromEvent, interval, map, merge, of, timeout } from 'rxjs';
import { SyncEngine } from 'src/app/core/offline/sync-engine.service';
import { SessionService } from '../sessionService/session.service';

/** how often an open tab asks the server for a new app version (besides start and «tab visible again») */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;
/** «Jetzt aktualisieren» waits at most this long for a running outbox sync (a photo upload would be sent again) */
export const SYNC_WAIT_MS = 30_000;
/** activateUpdate() that does not settle within this time no longer blocks the reload */
export const ACTIVATE_TIMEOUT_MS = 10_000;
/** at most one automatic reload on an unrecoverable SW state within this window (no reload loop) */
export const RECOVER_RELOAD_GUARD_MS = 5 * 60_000;
/** sessionStorage key: time of the last automatic reload on an unrecoverable SW state */
export const RECOVER_RELOAD_KEY = 'bavox.swRecoverReloadAt';
/** reloads the page; a token so specs never reload the Karma page */
export const APP_RELOAD = new InjectionToken<() => void>('APP_RELOAD', {
  providedIn: 'root',
  factory: () => () => document.location.reload(),
});

/**
 * The only place that follows the Angular service worker's updates (F1): checks for a new version at start, when
 * the tab becomes visible and every 30 min (only with the SW on and online), offers a ready version (AppComponent's
 * version modal) and activates it on request after the running sync. No automatic reload on VERSION_READY: open
 * forms would lose their input. An unrecoverable SW state reloads at once (at most once per 5 min per tab).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private readonly syncEngine = inject(SyncEngine);
  private readonly session = inject(SessionService);
  private readonly reload = inject(APP_RELOAD);

  private readonly readySubject = new BehaviorSubject<boolean>(false);
  /** true while a new version is ready and not dismissed */
  readonly updateReady$ = this.readySubject.asObservable();
  private readonly updatingSubject = new BehaviorSubject<boolean>(false);
  /** true from «Jetzt aktualisieren» until the reload (waiting for the sync) */
  readonly updating$ = this.updatingSubject.asObservable();

  /** test seams */
  isOnline: () => boolean = () => navigator.onLine;
  isVisible: () => boolean = () => document.visibilityState === 'visible';
  now: () => number = () => Date.now();

  private subscription: Subscription | null = null;

  start(): void {
    if (this.subscription || !this.swUpdate.isEnabled) return;
    const subscription = new Subscription();
    this.subscription = subscription;
    subscription.add(
      this.swUpdate.versionUpdates
        .pipe(filter((event: VersionEvent): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe((event) => void this.onVersionReady(event)),
    );
    subscription.add(this.swUpdate.unrecoverable.subscribe((event) => void this.recover(event.reason)));
    subscription.add(
      merge(
        of(null),
        fromEvent(document, 'visibilitychange').pipe(filter(() => this.isVisible())),
        interval(UPDATE_CHECK_INTERVAL_MS),
      ).subscribe(() => void this.check()),
    );
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** «Schliessen»: the modal comes back with the next VERSION_READY (next deploy) or after a reload */
  dismiss(): void {
    this.readySubject.next(false);
  }

  /** «Jetzt aktualisieren»: wait for a running sync (max 30 s), activate the new version, reload */
  async activate(): Promise<void> {
    if (this.updatingSubject.value) return;
    this.updatingSubject.next(true);
    await firstValueFrom(
      this.syncEngine.state$.pipe(
        filter((state) => !state.running),
        map(() => undefined),
        timeout({ first: SYNC_WAIT_MS, with: () => of(undefined) }),
      ),
      { defaultValue: undefined },
    );
    // a failed or hanging activateUpdate() must not keep the button on «Wird aktualisiert …»: the reload
    // loads the new version anyway (navigation request strategy freshness)
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.swUpdate.activateUpdate(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error(`timeout after ${ACTIVATE_TIMEOUT_MS} ms`)), ACTIVATE_TIMEOUT_MS);
        }),
      ]);
    } catch (error) {
      console.warn('[AppUpdateService] activateUpdate fehlgeschlagen, lade trotzdem neu', error);
    } finally {
      clearTimeout(timer);
    }
    this.reload();
  }

  private async check(): Promise<void> {
    if (!this.isOnline()) return;
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.warn('[AppUpdateService] checkForUpdate fehlgeschlagen', error);
    }
  }

  private async onVersionReady(event: VersionReadyEvent): Promise<void> {
    console.info(`[AppUpdateService] neue Version bereit: ${event.currentVersion.hash} → ${event.latestVersion.hash}`);
    // whatever the old worker wrote into a data cache must not outlive the switch to the new version
    await this.session.clearApiCache();
    this.readySubject.next(true);
  }

  private async recover(reason: string): Promise<void> {
    console.warn('[AppUpdateService] Service Worker in nicht wiederherstellbarem Zustand, lade neu', reason);
    await this.session.clearApiCache();
    // no reload loop when every load ends in the same broken state (e.g. a deploy whose hashes never match):
    // at most one automatic reload per RECOVER_RELOAD_GUARD_MS, otherwise only offer the update (user decides)
    const last = this.readRecoverReload();
    if (last !== null && this.now() - last < RECOVER_RELOAD_GUARD_MS) {
      console.warn('[AppUpdateService] erneut nicht wiederherstellbar kurz nach dem letzten Neuladen, kein automatischer Reload');
      this.readySubject.next(true);
      return;
    }
    this.writeRecoverReload(this.now());
    this.reload();
  }

  private readRecoverReload(): number | null {
    try {
      const value = Number(sessionStorage.getItem(RECOVER_RELOAD_KEY));
      return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
      return null; // storage unusable: no guard, the reload still happens
    }
  }

  private writeRecoverReload(at: number): void {
    try {
      sessionStorage.setItem(RECOVER_RELOAD_KEY, String(at));
    } catch {
      // storage unusable: reload without guard
    }
  }
}
